import { MDXProvider } from '@mdx-js/react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import React from 'react'

import BaseLayout from '@/layout/BaseLayout/BaseLayout'
import PageInfo from '@/components/common/PageInfo/PageInfo'
import FaqJsonLd from '@/components/seo/FaqJsonLd'
import { getPageTitle } from '@/utils/helpers'
import type { SeoMeta } from './types'
import mdxComponents from './mdxComponents'
import { useSeoLinks } from './seoLinks'
import { SeoSearchCategoryContext, seoSearchCategory } from './searchCategory'
import styles from './SeoContent.module.scss'

type SeoHeadProps = { meta: SeoMeta }

/** Per-page metadata. next/head lets the later tags win over BaseLayout's defaults. */
const SeoHead = ({ meta }: SeoHeadProps) => {
  const { locale } = useRouter()

  // 59 of the 70 delivered titles already end in "– Statusfälgar". Running those through
  // getPageTitle would repeat the brand and push the median title to ~101 characters,
  // well past where Google truncates. Only the titles that lack it get the suffix, in
  // the language of the page rather than the default.
  const title = /statusfälgar/i.test(meta.title) ? meta.title : getPageTitle(meta.title, locale)

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={meta.description} />
      {useSeoLinks(meta)}
    </Head>
  )
}

/**
 * Shifts every heading down one level. The category roots already carry an h1 above
 * their search, so their MDX copy would otherwise render a second one.
 */
const demotedHeadings = { h1: 'h2', h2: 'h3', h3: 'h4', h4: 'h5', h5: 'h6' } as const

// Both maps are module constants: MDXProvider memoises on the identity of what it is
// handed, so a fresh object per render would invalidate the whole MDX subtree each time.
const demotedMdxComponents = { ...mdxComponents, ...demotedHeadings }

type SeoContentProps = {
  children: React.ReactNode
  /** Set on the category roots, whose copy sits beneath an existing page heading. */
  demoteHeadings?: boolean
}

/**
 * Supplies the component map to MDX. Split out so the three category roots can drop
 * their copy into the existing search pages without a second BaseLayout.
 */
export const SeoContent = ({ children, demoteHeadings }: SeoContentProps) => (
  <MDXProvider components={demoteHeadings ? demotedMdxComponents : mdxComponents}>
    <div className={styles.component}>{children}</div>
  </MDXProvider>
)

type Props = {
  meta: SeoMeta
  children: React.ReactNode
}

/** Wrapper for a generated SEO page. Roots reuse SeoContent directly instead. */
export const SeoPage = ({ meta, children }: Props) => (
  <BaseLayout>
    <SeoHead meta={meta} />
    {meta.faq && <FaqJsonLd faq={meta.faq} />}
    <main className={styles.page}>
      <PageInfo>
        <SeoSearchCategoryContext.Provider value={seoSearchCategory(meta.url)}>
          <SeoContent>{children}</SeoContent>
        </SeoSearchCategoryContext.Provider>
      </PageInfo>
    </main>
  </BaseLayout>
)

export default SeoPage
